import { PayNowScreenModal } from "./paynow-screen-modal";
import { SelectPaymentScreenModal } from "./select-payment-screen-modal";
import {
  ACCOUNT_NUMBER,
  UEN_NUMBER,
  MERCHANT_NAME,
} from "@/constants/invoices";
import { copyClipboard } from "@/lib/helpers/copy-clipboard";
import type { PaymentType } from "@/store/invoices";
import { useInvoiceState } from "@/store/invoices";
import { PaymentMethodScreenModal } from "@/types/invoice";
import { CreditCardScreenModal } from "./credit-card-screen-modal";
import { useUpdatePaymentIndicatedStore } from "@/store/invoices/updatePaymentIndicated";
import { useRoute } from "@/components/shared/router";
import { generatePaymentQRUrl, getErrorMessage } from "@/lib/helpers/string";
import { showToast } from "@/components/ui/toast/show-toast";
import type { ClientApplyCreditFormData } from "@/store/profile/forms/clientApplyCreditForm";
import { useClientApplyCreditForm } from "@/store/profile/forms/clientApplyCreditForm";
import { useEffect, useState } from "react";
import { useClientApplyCreditStore } from "@/store/profile/clientApplyCredit";
import { formatPrice } from "@/lib/helpers/number";
import { useAuthState } from "@/store/auth";
import { getAccountCreditBalance } from "../lib";
import { useInvoiceDetailStore } from "@/store/invoices/invoiceDetail";
import { getPlatform } from "@/lib/utils";
import { useDebounceValue } from "@/components/hooks/use-debounce-value";
import { AddCreditCardScreenModal } from "./add-credit-card-screen-modal";
import { ApplyVoucherModal } from "../apply-voucher-modal";
import type { InvoiceDetailData } from "../invoice-detail/lib";
import { DepartmentCreditType } from "./type";
import {
  CreditTypeEnum,
  InvoiceSourceTypeEnum,
  PackageDepartmentEnum,
} from "@/__generated__/graphql";
import { useClientCreditAccounts } from "@/gqlhooks/profile/useClientCreditAccounts";
import { useClientStore } from "@/store/auth/client";

const getDepartmentCreditMeta = (
  departments: PackageDepartmentEnum[],
): {
  type: DepartmentCreditType;
  linkedName: "MASSAGE" | "HOME_BEAUTY" | "PET_CARE";
  label: string;
} | null => {
  if (departments.includes(PackageDepartmentEnum.Massage)) {
    return { type: "massage", linkedName: "MASSAGE", label: "Massage" };
  }
  if (departments.includes(PackageDepartmentEnum.HomeBeauty)) {
    return { type: "beauty", linkedName: "HOME_BEAUTY", label: "Beauty" };
  }
  if (departments.includes(PackageDepartmentEnum.PetCare)) {
    return { type: "pet", linkedName: "PET_CARE", label: "Pet Care" };
  }
  return null;
};

export const PaymentMethodContainer = () => {
  const { push, pull } = useRoute();
  const platform = getPlatform();

  const {
    data: {
      invoiceId,
      baseAmount,
      amount,
      paidAmount,
      balance,
      selectPaymentScreenModal,
      paynowScreenModal,
      creditCardScreenModal,
      addCreditCardScreenModal,
      topUpCreditPackageId,
      sourceType,
      appliedVoucher,
      applyMainCredit,
      applyMassageCredit,
      appliedMainCreditAmount,
      appliedMassageCreditAmount,
      currentBalance,
      showVoucherModal,
      invoiceDepartments,
    },
    showPaymentMethodModal,
    setTopUpCreditPackageId,
    setCurrentBalance,
    setAppliedMainCreditAmount,
    setAppliedMassageCreditAmount,
    setApplyMainCredit,
    setApplyMassageCredit,
  } = useInvoiceState();

  const {
    data: {
      userInfo: { id, paymentMethod },
    },
  } = useAuthState();

  const { data, fetch: invoiceDetail, refetch } = useInvoiceDetailStore();
  const { creditAccounts } = useClientCreditAccounts(id);

  useEffect(() => {
    if (invoiceId) {
      invoiceDetail({ requestPayload: { id: invoiceId } });
    }
  }, [invoiceId]);

  const [selectPayment, setSelectPayment] = useState<PaymentType>("");

  const { fetch: updatePayment, loading: updatePaymentLoading } =
    useUpdatePaymentIndicatedStore();
  const { fetch: clientApplyCredit, loading: clientApplyLoading } =
    useClientApplyCreditStore();
  const { fetch: client } = useClientStore();

  const paymentQrUri =
    data?.qrCodeUrl ??
    generatePaymentQRUrl({
      invoiceId,
      balance: currentBalance,
    });

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useClientApplyCreditForm();

  const mainCreditBalance = watch("mainCreditBalance");
  const massageCreditBalance = watch("massageCreditBalance");

  const debouncedMainBalance = useDebounceValue<string>(
    mainCreditBalance || "",
    500,
  );
  const debouncedMassageBalance = useDebounceValue<string>(
    massageCreditBalance || "",
    500,
  );

  const mainBalance = getAccountCreditBalance({
    creditAccounts,
    type: CreditTypeEnum.General,
  });

  const selectedDepartmentCredit = getDepartmentCreditMeta(invoiceDepartments);
  const departmentCreditType =
    sourceType === InvoiceSourceTypeEnum.CreditAccountTopUp
      ? null
      : (selectedDepartmentCredit?.type ?? null);
  const departmentCreditLabel = selectedDepartmentCredit?.label ?? "Department";
  const departmentLinkedName = selectedDepartmentCredit?.linkedName;

  const creditBalance =
    creditAccounts.find(
      (credit) =>
        credit.creditType === CreditTypeEnum.Department &&
        credit.linkedObject?.__typename === "Department" &&
        credit.linkedObject.name === departmentLinkedName,
    )?.balance ?? 0;

  // Handle main credit balance changes
  useEffect(() => {
    const parsedAmount = parseFloat(debouncedMainBalance || "0");

    if (applyMainCredit) {
      if (parsedAmount > 0 && !errors.mainCreditBalance) {
        setAppliedMainCreditAmount(parsedAmount);
      } else {
        setAppliedMainCreditAmount(0);
      }
    }
  }, [debouncedMainBalance, applyMainCredit, errors.mainCreditBalance]);

  // Handle massage credit balance changes
  useEffect(() => {
    const parsedAmount = parseFloat(debouncedMassageBalance || "0");

    if (applyMassageCredit) {
      if (parsedAmount > 0 && !errors.massageCreditBalance) {
        setAppliedMassageCreditAmount(parsedAmount);
      } else {
        setAppliedMassageCreditAmount(0);
      }
    }
  }, [
    debouncedMassageBalance,
    applyMassageCredit,
    errors.massageCreditBalance,
  ]);

  // Calculate current balance based on applied credits
  useEffect(() => {
    const totalAppliedCredit =
      appliedMainCreditAmount + appliedMassageCreditAmount;
    const newBalance = balance - totalAppliedCredit;
    setCurrentBalance(Math.max(0, newBalance));
  }, [appliedMainCreditAmount, appliedMassageCreditAmount, balance]);

  // Reset amounts when switches are turned off
  useEffect(() => {
    if (!applyMainCredit) {
      setAppliedMainCreditAmount(0);
      setValue("mainCreditBalance", "");
    }
  }, [applyMainCredit]);

  useEffect(() => {
    if (!applyMassageCredit) {
      setAppliedMassageCreditAmount(0);
      setValue("massageCreditBalance", "");
    }
  }, [applyMassageCredit]);

  const isFullCreditAccountPayment = currentBalance === 0;

  useEffect(() => {
    if (isFullCreditAccountPayment) {
      setSelectPayment("");
    } else {
      setSelectPayment("credit-card");
    }
  }, [isFullCreditAccountPayment]);

  const onApplyAllMain = () => {
    const remainingBalance = balance;
    setApplyMassageCredit(false);
    if (mainBalance >= remainingBalance) {
      setValue("mainCreditBalance", remainingBalance.toFixed(2).toString());
    } else {
      setValue("mainCreditBalance", mainBalance.toFixed(2).toString());
    }
  };

  const onApplyAllMassage = () => {
    const remainingBalance = balance;
    setApplyMainCredit(false);
    if (creditBalance >= remainingBalance) {
      setValue("massageCreditBalance", remainingBalance.toFixed(2).toString());
    } else {
      setValue("massageCreditBalance", creditBalance.toFixed(2).toString());
    }
  };

  const onCopyAccountNumber = () => {
    copyClipboard(ACCOUNT_NUMBER);
  };

  const onCopyUenNumber = () => {
    copyClipboard(UEN_NUMBER);
  };

  const onCopyReferenceNumber = () => {
    copyClipboard(invoiceId ? `P${invoiceId}` : "");
  };

  const onConfirmTransfer = async () => {
    await updatePayment(
      { requestPayload: { input: { invoiceId } } },
      { selfHandleError: true },
    );

    const successMsg = `Success update payment Invoice ${invoiceId}`;
    showToast({
      title: successMsg,
      type: "success",
    });

    client({ requestPayload: { id } });
    showPaymentMethodModal();
    push({ pageKey: "invoices" });
  };

  const isCreditCard = selectPayment === "credit-card";
  const isPayNow = selectPayment === "paynow";

  // Extract validation logic
  const validateCreditAmount = (
    amount: number,
    available: number,
    balance: number,
    fieldName: keyof ClientApplyCreditFormData,
  ): boolean => {
    if (amount === 0) {
      setError(fieldName, {
        message: "Please check your input to ensure it is greater than zero.",
      });
      return false;
    }

    if (amount > balance) {
      setError(fieldName, {
        message:
          "Please check your input and ensure it doesn't exceed the total amount.",
      });
      return false;
    }

    if (amount > available) {
      setError(fieldName, {
        message:
          "You don't have enough credits. Please enter a smaller amount.",
      });
      return false;
    }

    return true;
  };

  // Extract credit allocation building
  const buildCreditAllocations = (data: {
    mainCreditBalance: string;
    massageCreditBalance: string;
  }) => {
    const allocations = [];

    if (applyMainCredit && data.mainCreditBalance) {
      const mainAmount = parseFloat(data.mainCreditBalance);
      if (
        !validateCreditAmount(
          mainAmount,
          mainBalance,
          balance,
          "mainCreditBalance",
        )
      ) {
        return null;
      }

      const mainCreditAccount = creditAccounts.find(
        (acc) => acc.creditType === CreditTypeEnum.General,
      );

      if (mainCreditAccount) {
        allocations.push({
          creditAccountId: mainCreditAccount.id,
          amount: mainAmount,
        });
      }
    }

    if (
      applyMassageCredit &&
      data.massageCreditBalance &&
      departmentLinkedName
    ) {
      const massageAmount = parseFloat(data.massageCreditBalance);
      if (
        !validateCreditAmount(
          massageAmount,
          creditBalance,
          balance,
          "massageCreditBalance",
        )
      ) {
        return null;
      }

      const massageCreditAccount = creditAccounts.find(
        (acc) =>
          acc.creditType === CreditTypeEnum.Department &&
          acc.linkedObject?.__typename === "Department" &&
          acc.linkedObject.name === departmentLinkedName,
      );

      if (massageCreditAccount) {
        allocations.push({
          creditAccountId: massageCreditAccount.id,
          amount: massageAmount,
        });
      }
    }

    return allocations;
  };

  // Extract payment processing
  const processPayment = async (
    creditAllocations: {
      creditAccountId: string;
      amount: number;
    }[],
  ) => {
    try {
      const res = await clientApplyCredit(
        {
          requestPayload: {
            input: {
              invoiceId: invoiceId,
              creditAllocations,
            },
          },
        },
        { selfHandleError: true },
      );

      if (res.error) throw new Error(res.error);

      showToast({
        title: "Payment successful using account credit",
        type: "success",
      });

      refetch();

      if (isFullCreditAccountPayment) {
        showPaymentMethodModal();
        push({ pageKey: "invoices" });
      } else {
        showPaymentMethodModal(PaymentMethodScreenModal.PAYNOW);
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error apply credit account");
      showToast({ title: errMsg, type: "error" });
    }
  };

  const onSubmitAndPay = handleSubmit(async (data) => {
    const creditAllocations = buildCreditAllocations(data);

    if (creditAllocations === null) {
      return; // Validation failed
    }

    if (isCreditCard) {
      showPaymentMethodModal(
        paymentMethod
          ? PaymentMethodScreenModal.CREDIT_CARD
          : PaymentMethodScreenModal.ADD_CREDIT_CARD,
      );
      return;
    }

    if (isFullCreditAccountPayment || isPayNow) {
      if (creditAllocations.length === 0) {
        showToast({
          title: "Please apply at least one credit",
          type: "error",
        });
        return;
      }

      await processPayment(creditAllocations);
    }
  });

  const onPay = () => {
    if (applyMainCredit || applyMassageCredit) {
      onSubmitAndPay();
    } else if (isCreditCard) {
      showPaymentMethodModal(
        paymentMethod
          ? PaymentMethodScreenModal.CREDIT_CARD
          : PaymentMethodScreenModal.ADD_CREDIT_CARD,
      );
    } else if (isPayNow) {
      showPaymentMethodModal(PaymentMethodScreenModal.PAYNOW);
    }
  };

  const onClosePayNow = () => {
    if (platform === "native") {
      pull();
    } else {
      showPaymentMethodModal();
    }
  };

  const onCloseSelectPayment = () => {
    reset();
    showPaymentMethodModal();
    setTopUpCreditPackageId(null);
    if (platform === "native") {
      setCurrentBalance(balance);
      pull();
    }
  };

  const hasAnyCreditsApplied = applyMainCredit || applyMassageCredit;

  const onPayButtonText = isPayNow
    ? hasAnyCreditsApplied
      ? "Use Credit & Get QR"
      : "Get QR"
    : "Pay";

  return (
    <>
      <SelectPaymentScreenModal
        control={control}
        open={selectPaymentScreenModal}
        onCloseSelectPayment={onCloseSelectPayment}
        loading={clientApplyLoading}
        onPay={onPay}
        onPayButtonText={onPayButtonText}
        onApplyAllMain={onApplyAllMain}
        onApplyAllMassage={onApplyAllMassage}
        selectPayment={selectPayment}
        setSelectPayment={setSelectPayment}
        baseAmount={formatPrice(baseAmount)}
        paidAmount={paidAmount}
        amount={amount}
        balance={formatPrice(currentBalance)}
        appliedVoucher={appliedVoucher}
        applyMainCredit={appliedMainCreditAmount > 0}
        applyMassageCredit={appliedMassageCreditAmount > 0}
        appliedMainCredit={formatPrice(appliedMainCreditAmount)}
        appliedMassageCredit={formatPrice(appliedMassageCreditAmount)}
        isSelectCreditCard={isCreditCard}
        isSelectPayNow={isPayNow}
        disableApplyAllMain={isFullCreditAccountPayment}
        disableApplyAllMassage={isFullCreditAccountPayment}
        departmentCreditType={departmentCreditType}
        departmentCreditLabel={departmentCreditLabel}
        isTopUpPayment={topUpCreditPackageId}
      />

      <PayNowScreenModal
        open={paynowScreenModal}
        invoiceId={invoiceId}
        accountNumber={ACCOUNT_NUMBER}
        uenNumber={UEN_NUMBER}
        merchantName={MERCHANT_NAME}
        onCopyAccountNumber={onCopyAccountNumber}
        onCopyUenNumber={onCopyUenNumber}
        onCopyReference={onCopyReferenceNumber}
        paymentQrUri={paymentQrUri}
        onClose={onClosePayNow}
        onConfirmTransfer={onConfirmTransfer}
        loading={updatePaymentLoading}
        baseAmount={formatPrice(baseAmount)}
        amount={amount}
        balance={formatPrice(currentBalance)}
        appliedVoucher={appliedVoucher}
      />

      <CreditCardScreenModal
        open={creditCardScreenModal}
        onClose={() =>
          showPaymentMethodModal(PaymentMethodScreenModal.SELECT_PAYMENT)
        }
      />

      <AddCreditCardScreenModal
        open={addCreditCardScreenModal}
        onClose={() =>
          showPaymentMethodModal(PaymentMethodScreenModal.SELECT_PAYMENT)
        }
      />

      <ApplyVoucherModal
        invoice={data as InvoiceDetailData}
        open={showVoucherModal}
      />
    </>
  );
};
